import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import "./UserDetails.css";
import Loader from "../../Loader/Loader";
import {
  getCustomerProfile,
  suspendCustomer,
  banCustomer,
  creditCustomerAccount,
  refundOrder,
  sendMessageToCustomer,
} from "../../service/Mainservice";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PaymentIcon from "@mui/icons-material/Payment";
import OrderIcon from "@mui/icons-material/Receipt";
import ReviewsIcon from "@mui/icons-material/RateReview";
import SupportIcon from "@mui/icons-material/SupportAgent";
import ChatIcon from "@mui/icons-material/Chat"; // ✅ NEW
import BlockIcon from "@mui/icons-material/Block";
import SendIcon from "@mui/icons-material/Send";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import RefundIcon from "@mui/icons-material/CurrencyExchange";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Modal from "./Model";
import ChatComponent from "../../component/Chat/ChatComponent"; // ✅ NEW

const UserDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const customerId = location.state?.id;

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("personal");
  const [expandedOrders, setExpandedOrders] = useState({});

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [modalData, setModalData] = useState({});

  useEffect(() => {
    if (customerId) {
      fetchProfile();
    } else {
      navigate("/Users_List");
    }
  }, [customerId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await getCustomerProfile(customerId);
      setUser(res.data.data);
    } catch (error) {
      console.error(error);
      if (error?.status === 401) {
        localStorage.clear();
        navigate("/");
      } else {
        alert("Failed to load customer profile");
      }
    } finally {
      setLoading(false);
    }
  };

  // ----- Action Handlers -----
  const handleSuspend = async () => {
    const duration = modalData.durationDays || 7;
    const reason = modalData.reason || "No reason provided";
    try {
      await suspendCustomer(customerId, duration, reason);
      alert(`Customer suspended for ${duration} days`);
      fetchProfile();
    } catch (error) {
      alert("Failed to suspend customer");
    }
    setModalOpen(false);
  };

  const handleBan = async () => {
    const isBanned = user?.profile?.isBlocked;
    const action = isBanned ? "unban" : "ban";
    const reason = modalData.reason || "No reason provided";
    try {
      await banCustomer(customerId, action, reason);
      alert(
        `Customer ${action === "ban" ? "banned" : "unbanned"} successfully`,
      );
      fetchProfile();
    } catch (error) {
      alert(`Failed to ${action} customer`);
    }
    setModalOpen(false);
  };

  const handleCredit = async () => {
    const amount = modalData.amount;
    if (!amount || amount <= 0) {
      alert("Please enter a valid amount");
      return;
    }
    const reason = modalData.reason || "Admin credit";
    try {
      await creditCustomerAccount(customerId, amount, reason);
      alert(`Credited $${amount} successfully`);
      fetchProfile();
    } catch (error) {
      alert("Failed to credit account");
    }
    setModalOpen(false);
  };

  const handleRefund = async () => {
    const orderId = modalData.orderId;
    if (!orderId) {
      alert("Please select an order to refund");
      return;
    }
    const reason = modalData.reason || "Refund requested";
    try {
      await refundOrder(orderId, reason);
      alert("Refund processed successfully");
      fetchProfile();
    } catch (error) {
      alert("Failed to process refund");
    }
    setModalOpen(false);
  };

  const handleSendMessage = async () => {
    const message = modalData.message;
    if (!message) {
      alert("Please enter a message");
      return;
    }
    try {
      await sendMessageToCustomer(customerId, message);
      alert("Message sent successfully");
    } catch (error) {
      alert("Failed to send message");
    }
    setModalOpen(false);
  };

  const openModal = (action, initialData = {}) => {
    setModalAction(action);
    setModalData(initialData);
    setModalOpen(true);
  };

  // Toggle order product expansion
  const toggleOrderExpand = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  // ----- Modal Content Renderer -----
  const renderModalContent = () => {
    const renderReasonField = () => (
      <div className="modal_field">
        <label>Reason (optional)</label>
        <textarea
          placeholder="Provide a reason for this action..."
          value={modalData.reason || ""}
          onChange={(e) =>
            setModalData({ ...modalData, reason: e.target.value })
          }
          className="modal_textarea"
          rows="3"
        />
      </div>
    );

    switch (modalAction) {
      case "suspend":
        return (
          <div>
            <h3>Suspend Customer</h3>
            <p>
              The customer will not be able to place orders for the specified
              duration.
            </p>
            <div className="modal_field">
              <label>Duration (days)</label>
              <input
                type="number"
                min="1"
                max="365"
                value={modalData.durationDays || 7}
                onChange={(e) =>
                  setModalData({
                    ...modalData,
                    durationDays: parseInt(e.target.value) || 7,
                  })
                }
                className="modal_input"
              />
            </div>
            {renderReasonField()}
            <div className="modal_actions">
              <button onClick={handleSuspend} className="btn_danger">
                Suspend
              </button>
              <button
                onClick={() => setModalOpen(false)}
                className="btn_secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        );

      case "ban": {
        const isBanned = user?.profile?.isBlocked;
        const actionLabel = isBanned ? "Unban" : "Ban";
        return (
          <div>
            <h3>{actionLabel} Customer</h3>
            <p>
              {isBanned
                ? "This will reactivate the customer's account."
                : "This action permanently bans the customer from the platform."}
            </p>
            <div className="modal_actions">
              <button onClick={handleBan} className="btn_danger">
                {actionLabel}
              </button>
              <button
                onClick={() => setModalOpen(false)}
                className="btn_secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        );
      }

      case "credit":
        return (
          <div>
            <h3>Credit Account</h3>
            <div className="modal_field">
              <label>Amount ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Enter amount"
                value={modalData.amount || ""}
                onChange={(e) =>
                  setModalData({ ...modalData, amount: e.target.value })
                }
                className="modal_input"
              />
            </div>
            {renderReasonField()}
            <div className="modal_actions">
              <button onClick={handleCredit} className="btn_primary">
                Credit
              </button>
              <button
                onClick={() => setModalOpen(false)}
                className="btn_secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        );

      case "refund":
        return (
          <div>
            <h3>Refund Order</h3>
            <div className="modal_field">
              <label>Select Order</label>
              <select
                value={modalData.orderId || ""}
                onChange={(e) =>
                  setModalData({ ...modalData, orderId: e.target.value })
                }
                className="modal_input"
              >
                <option value="">Choose an order</option>
                {user?.orderHistory?.map((order) => (
                  <option key={order.id} value={order.id}>
                    #{order.orderNumber || "N/A"} - ${order.totalPrice}
                  </option>
                ))}
              </select>
            </div>
            {renderReasonField()}
            <div className="modal_actions">
              <button onClick={handleRefund} className="btn_primary">
                Refund
              </button>
              <button
                onClick={() => setModalOpen(false)}
                className="btn_secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        );

      case "message":
        return (
          <div>
            <h3>Send Message</h3>
            <textarea
              placeholder="Type your message..."
              value={modalData.message || ""}
              onChange={(e) =>
                setModalData({ ...modalData, message: e.target.value })
              }
              className="modal_textarea"
              rows="4"
            />
            <div className="modal_actions">
              <button onClick={handleSendMessage} className="btn_primary">
                Send
              </button>
              <button
                onClick={() => setModalOpen(false)}
                className="btn_secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ----- Dynamic Action Buttons based on active tab -----
  const renderActionButtons = () => {
    switch (activeTab) {
      case "personal":
        return (
          <>
            <button className="action_btn" onClick={() => openModal("suspend")}>
              <BlockIcon /> Suspend
            </button>
            <button className="action_btn" onClick={() => openModal("ban")}>
              <BlockIcon style={{ color: isBanned ? "green" : "red" }} />
              {isBanned ? "Unban" : "Ban"}
            </button>
          </>
        );
      case "orders":
        return (
          <>
            <button
              className="action_btn"
              onClick={() => openModal("refund")}
              disabled={!orderHistory?.length}
            >
              <RefundIcon /> Refund
            </button>
            <button className="action_btn" onClick={() => openModal("credit")}>
              <CreditCardIcon /> Credit
            </button>
          </>
        );
      case "tickets":
        return (
          <button className="action_btn" onClick={() => openModal("message")}>
            <SendIcon /> Send Message
          </button>
        );
      case "messages":
        return null; // ✅ No extra actions – chat is built-in
      default:
        return null;
    }
  };

  // ----- Render -----
  if (loading) return <Loader />;
  if (!user) return <div className="error_msg">User not found</div>;

  const {
    profile,
    addresses,
    paymentMethods,
    orderHistory,
    reviewsLeft,
    supportTickets,
  } = user;
  let statusBadge = "active";
  let statusText = "Active";
  if (profile.isBlocked) {
    statusBadge = "banned";
    statusText = "Banned";
  } else if (profile.suspendedUntil) {
    statusBadge = "suspended";
    statusText = "Suspended";
  }
  const isBanned = profile.isBlocked;

  return (
    <>
      <div className="userdetails_container">
        {/* Header */}
        <div className="details_header">
          <button className="back_btn" onClick={() => navigate("/Users_List")}>
            ← Back to Users
          </button>
          <h2>Customer Details</h2>
        </div>

        {/* Profile Summary */}
        <div className="profile_summary">
          <div className="avatar">
            {profile.image ? (
              <img src={profile.image} alt={profile.fullName} />
            ) : (
              <PersonOutlineIcon style={{ fontSize: 60 }} />
            )}
          </div>
          <div className="summary_info">
            <h3>{profile.fullName}</h3>
            <p>
              <EmailIcon fontSize="small" /> {profile.email}
            </p>
            <p>
              <PhoneIcon fontSize="small" /> {profile.mobilenumber}
            </p>
            <p>
              Status:{" "}
              <span className={`badge ${statusBadge}`}>{statusText}</span>
            </p>
            <p>Joined: {new Date(profile.createdAt).toLocaleDateString()}</p>
          </div>
          <div className="summary_stats">
            <div>
              <strong>{orderHistory?.length || 0}</strong>
              <span>Total Orders</span>
            </div>
            <div>
              <strong>
                $
                {orderHistory?.reduce(
                  (sum, o) => sum + parseFloat(o.totalPrice || 0),
                  0,
                ) || 0}
              </strong>
              <span>Total Spent</span>
            </div>
            <div>
              <strong>${profile.walletCredit || 0}</strong>
              <span>Wallet Credit</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <button
            className={activeTab === "personal" ? "tab_active" : ""}
            onClick={() => setActiveTab("personal")}
          >
            <PersonOutlineIcon /> Personal
          </button>
          <button
            className={activeTab === "orders" ? "tab_active" : ""}
            onClick={() => setActiveTab("orders")}
          >
            <OrderIcon /> Orders ({orderHistory?.length || 0})
          </button>
          <button
            className={activeTab === "reviews" ? "tab_active" : ""}
            onClick={() => setActiveTab("reviews")}
          >
            <ReviewsIcon /> Reviews ({reviewsLeft?.length || 0})
          </button>
          <button
            className={activeTab === "tickets" ? "tab_active" : ""}
            onClick={() => setActiveTab("tickets")}
          >
            <SupportIcon /> Support ({supportTickets?.length || 0})
          </button>
          {/* ✅ NEW MESSAGES TAB */}
          <button
            className={activeTab === "messages" ? "tab_active" : ""}
            onClick={() => setActiveTab("messages")}
          >
            <ChatIcon /> Messages
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab_content">
          {activeTab === "personal" && (
            <div className="personal_info">
              <div className="info_section">
                <h4>Addresses</h4>
                {addresses?.length ? (
                  addresses.map((addr, i) => (
                    <div key={i} className="address_item">
                      <LocationOnIcon fontSize="small" />
                      <span>
                        {addr.line1}, {addr.city}, {addr.state} -{" "}
                        {addr.postal_code}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="no_data">No addresses saved</p>
                )}
              </div>

              <div className="info_section">
                <h4>Payment Methods</h4>
                {paymentMethods?.length ? (
                  paymentMethods.map((pm, i) => (
                    <div key={i} className="payment_item">
                      <PaymentIcon fontSize="small" />
                      <span>
                        {pm.brand} ending in {pm.last4} (exp {pm.expMonth}/
                        {pm.expYear})
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="no_data">No payment methods saved</p>
                )}
              </div>
            </div>
          )}

          {activeTab === "orders" && (
            <div className="order_history">
              {orderHistory?.length ? (
                orderHistory.map((order) => (
                  <div key={order.id} className="order_card">
                    <div
                      className="order_header"
                      onClick={() => toggleOrderExpand(order.id)}
                    >
                      <div className="order_summary">
                        <span className="order_number">
                          #{order.orderNumber || "N/A"}
                        </span>
                        <span className="order_date">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </span>
                        <span className="order_total">${order.totalPrice}</span>
                        <span
                          className={`badge ${order.orderStatus.toLowerCase()}`}
                        >
                          {order.orderStatus}
                        </span>
                      </div>
                      <button className="expand_btn">
                        {expandedOrders[order.id] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </button>
                    </div>
                    {expandedOrders[order.id] && (
                      <div className="order_details">
                        <p>
                          <strong>Payment Method:</strong> {order.paymentMethod}
                        </p>
                        <p>
                          <strong>Delivery Type:</strong>{" "}
                          {order.OrderDeliveryType || order.deliveryType}
                        </p>
                        {order.shippingAddress && (
                          <p>
                            <strong>Address:</strong> {order.shippingAddress}
                          </p>
                        )}
                        {order.products?.length > 0 && (
                          <div className="order_products">
                            <strong>Products:</strong>
                            <ul>
                              {order.products.map((p, idx) => (
                                <li key={idx}>
                                  {p.name} × {p.quantity} = ${p.totalPrice}
                                  {p.image && (
                                    <img
                                      src={p.image}
                                      alt={p.name}
                                      className="product_thumb"
                                    />
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="no_data">No orders yet</p>
              )}
            </div>
          )}

          {activeTab === "reviews" && (
            <div className="reviews">
              {reviewsLeft?.length ? (
                reviewsLeft.map((rev, i) => (
                  <div key={i} className="review_item">
                    <div className="review_header">
                      <strong>
                        {rev.reviewType === "vendor" ? "Vendor" : "Product"}{" "}
                        Review
                      </strong>
                      <span>⭐ {rev.rating}</span>
                    </div>
                    <p>{rev.feedBack}</p>
                    <small>
                      {new Date(rev.createdAt).toLocaleDateString()}
                    </small>
                  </div>
                ))
              ) : (
                <p className="no_data">No reviews left</p>
              )}
            </div>
          )}

          {activeTab === "tickets" && (
            <div className="tickets">
              {supportTickets?.length ? (
                supportTickets.map((ticket, i) => (
                  <div key={i} className="ticket_item">
                    <div className="ticket_header">
                      <strong>{ticket.subject}</strong>
                      <span className={`badge ${ticket.status.toLowerCase()}`}>
                        {ticket.status}
                      </span>
                    </div>
                    <p>{ticket.message}</p>
                    <small>
                      Created: {new Date(ticket.createdAt).toLocaleDateString()}
                    </small>
                  </div>
                ))
              ) : (
                <p className="no_data">No support tickets</p>
              )}
            </div>
          )}

          {/* ✅ NEW MESSAGES CONTENT */}
          {activeTab === "messages" && (
            <div className="messages_tab">
              <ChatComponent
                userId={customerId}
                userType="customer"
                title={`Chat with ${profile.fullName}`}
              />
            </div>
          )}
        </div>

        {/* Actions Bar - dynamic */}
        <div className="actions_bar">{renderActionButtons()}</div>
      </div>

      {/* Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        {renderModalContent()}
      </Modal>
    </>
  );
};

export default UserDetails;
